(() => {
  'use strict';

  const FILTERS = [
    { key: 'metal', label: 'Metal', attribute: 'data-metal' },
    { key: 'product-type', label: 'Product Type', attribute: 'data-product-type' },
    { key: 'purity', label: 'Purity', attribute: 'data-purity' },
    { key: 'thickness', label: 'Thickness', attribute: 'data-thickness' },
    { key: 'width', label: 'Width', attribute: 'data-width' },
    { key: 'diameter', label: 'Diameter', attribute: 'data-diameter' },
    { key: 'length', label: 'Length', attribute: 'data-length' },
  ];

  const URL_KEYS = FILTERS.map((filter) => `hpa_${filter.key.replace('-', '_')}`);
  URL_KEYS.push('hpa_search');

  const escapeHtml = (value) =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const initCollectionFilters = (root) => {
    if (!root || root.dataset.hpaInitialized === 'true') return;
    root.dataset.hpaInitialized = 'true';

    const section = root.closest('[data-hpa-collection-section]') || root.parentElement;
    const productGrid = section?.querySelector('[data-hpa-product-grid]');
    const variantGrid = section?.querySelector('[data-hpa-variant-grid]');
    const emptyState = section?.querySelector('[data-hpa-no-results]');
    const loadMore = section?.querySelector('[data-hpa-load-more]');
    const loadMoreButton = loadMore?.querySelector('button');
    const searchInput = root.querySelector('[data-hpa-search]');
    const countElement = root.querySelector('[data-hpa-count]');
    const clearButtons = root.querySelectorAll('[data-hpa-clear]');
    const mobileDrawer = root.querySelector('[data-hpa-mobile-drawer]');
    const mobileOverlay = root.querySelector('[data-hpa-mobile-overlay]');
    const pageSize = Math.max(parseInt(root.dataset.hpaPageSize || '16', 10), 1);
    const productCards = productGrid ? [...productGrid.querySelectorAll('[data-hpa-product-card]')] : [];
    const variantCards = variantGrid ? [...variantGrid.querySelectorAll('[data-hpa-variant-card]')] : [];
    const selections = Object.fromEntries(FILTERS.map((filter) => [filter.key, []]));
    const variantIndex = variantCards.map((card) => {
      const record = { card, title: (card.dataset.hpaTitle || card.textContent || "").toLowerCase(), price: Number(card.dataset.hpaPrice || 0) };
      FILTERS.forEach((filter) => {
        record[filter.key] = (card.getAttribute(filter.attribute) || '').trim();
      });
      return record;
    });

    let shownCount = 0;

    const sortBy = () => new URL(window.location.href).searchParams.get("sort_by") || "";

    const compareRecords = (first, second) => {
      const key = sortBy();
      if (key === "price-ascending" || key === "price-descending") {
        const difference = first.price - second.price;
        return key === "price-descending" ? -difference : difference;
      }
      if (key === "title-ascending" || key === "title-descending") {
        const difference = first.title.localeCompare(second.title, undefined, { sensitivity: "base" });
        return key === "title-descending" ? -difference : difference;
      }
      return 0;
    };

    const applyClientSort = () => {
      const key = sortBy();
      if (!["price-ascending", "price-descending", "title-ascending", "title-descending"].includes(key)) return;
      const productRecords = productCards.map((card) => ({
        card,
        title: (card.dataset.hpaTitle || card.textContent || "").toLowerCase(),
        price: Number(card.dataset.hpaPrice || 0),
      }));
      productRecords.sort(compareRecords);
      productCards.splice(0, productCards.length, ...productRecords.map((record) => record.card));
      variantIndex.sort(compareRecords);
      productCards.forEach((card) => productGrid?.appendChild(card));
      variantIndex.forEach((record) => variantGrid?.appendChild(record.card));
    };

    const hasActiveFilters = () => FILTERS.some((filter) => selections[filter.key].length > 0);
    const searchTerm = () => (searchInput?.value || '').trim().toLowerCase();

    const matchesRecord = (record, excludedFilter) => {
      for (const filter of FILTERS) {
        if (filter.key === excludedFilter) continue;
        const selected = selections[filter.key];
        if (selected.length && !selected.includes(record[filter.key])) return false;
      }
      const term = searchTerm();
      return !term || record.title.includes(term);
    };

    const matchingVariants = (excludedFilter) => variantIndex.filter((record) => matchesRecord(record, excludedFilter));

    const optionMarkup = (filter, mobile = false) => {
      const counts = {};
      matchingVariants(filter.key).forEach((record) => {
        const value = record[filter.key];
        if (value) counts[value] = (counts[value] || 0) + 1;
      });

      const values = Object.keys(counts).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      const selected = selections[filter.key];
      if (!values.length) return '';

      const optionClass = mobile ? 'hpa-mobile-filter-option' : 'hpa-filter-option';
      return values
        .map(
          (value) => `
            <label class="${optionClass}">
              <input type="checkbox" value="${escapeHtml(value)}" data-hpa-filter-value="${filter.key}"${selected.includes(value) ? ' checked' : ''}>
              <span class="hpa-filter-option__box" aria-hidden="true"></span>
              <span class="hpa-filter-option__label">${escapeHtml(value)}</span>
              <span class="hpa-filter-option__number">${counts[value]}</span>
            </label>`,
        )
        .join('');
    };

    applyClientSort();
    const renderFilterMenus = () => {
      FILTERS.forEach((filter) => {
        const selectedCount = selections[filter.key].length;
        root.querySelectorAll(`[data-hpa-filter="${filter.key}"]`).forEach((group) => {
          const menu = group.querySelector('[data-hpa-filter-menu]');
          if (!menu) return;
          const isMobile = group.classList.contains('hpa-mobile-filter-group');
          menu.innerHTML = optionMarkup(filter, isMobile);
          group.hidden = !menu.children.length;
          const count = group.querySelector('[data-hpa-filter-count]');
          if (count) count.textContent = selectedCount ? `(${selectedCount})` : '';
        });
      });
    };

    const updateCount = (count) => {
      if (!countElement) return;
      countElement.innerHTML = `<strong>${count}</strong> product${count === 1 ? '' : 's'}`;
    };

    const updateLoadMore = (matches) => {
      if (!loadMore || !loadMoreButton) return;
      loadMore.hidden = shownCount >= matches.length;
      loadMoreButton.textContent = `Load more (${Math.max(matches.length - shownCount, 0)})`;
    };

    const showCards = (cards, matches) => {
      cards.forEach((card) => {
        card.hidden = true;
        card.classList.remove('hpa-card-is-visible');
      });
      const visible = matches.slice(0, shownCount);
      visible.forEach((card) => {
        card.hidden = false;
        card.classList.add('hpa-card-is-visible');
      });
    };

    const showProductView = (preserveLimit = false) => {
      productGrid?.classList.remove('hpa-grid-is-hidden');
      variantGrid?.classList.add('hpa-grid-is-hidden');
      emptyState?.setAttribute('hidden', 'hidden');

      const term = searchTerm();
      const matches = productCards.filter((card) => {
        const title = (card.dataset.hpaTitle || card.textContent || '').toLowerCase();
        return !term || title.includes(term);
      });
      shownCount = Math.min(preserveLimit ? Math.max(shownCount, pageSize) : pageSize, matches.length);
      showCards(productCards, matches);
      updateCount(matches.length);
      updateLoadMore(matches);
    };

    const showVariantView = (preserveLimit = false) => {
      productGrid?.classList.add('hpa-grid-is-hidden');
      variantGrid?.classList.remove('hpa-grid-is-hidden');
      const matches = matchingVariants();
      shownCount = Math.min(preserveLimit ? Math.max(shownCount, pageSize) : pageSize, matches.length);
      showCards(variantCards, matches.map((record) => record.card));
      emptyState?.toggleAttribute('hidden', matches.length === 0);
      updateCount(matches.length);
      updateLoadMore(matches.map((record) => record.card));
    };

    const serializeState = () => {
      const url = new URL(window.location.href);
      URL_KEYS.forEach((key) => url.searchParams.delete(key));
      FILTERS.forEach((filter) => {
        const key = `hpa_${filter.key.replace('-', '_')}`;
        selections[filter.key].forEach((value) => url.searchParams.append(key, value));
      });
      if (searchTerm()) url.searchParams.set('hpa_search', searchInput.value.trim());
      return url;
    };

    const updateUrl = (replace = false) => {
      const url = serializeState();
      window.history[replace ? 'replaceState' : 'pushState']({ hpaCollection: true }, '', url.toString());
    };

    const readState = () => {
      const params = new URLSearchParams(window.location.search);
      FILTERS.forEach((filter) => {
        const key = `hpa_${filter.key.replace('-', '_')}`;
        selections[filter.key] = params.getAll(key);
      });
      if (searchInput) searchInput.value = params.get('hpa_search') || '';
    };

    const apply = ({ updateHistory = true } = {}) => {
      renderFilterMenus();
      if (hasActiveFilters()) showVariantView();
      else showProductView();

      const hasState = hasActiveFilters() || Boolean(searchTerm());
      clearButtons.forEach((button) => {
        button.hidden = !hasState;
      });
      if (updateHistory) updateUrl();
    };

    root.addEventListener('click', (event) => {
      const toggle = event.target.closest('.hpa-filter-dropdown__toggle, .hpa-mobile-filter-group__toggle');
      if (toggle) {
        const group = toggle.closest('[data-hpa-filter]');
        const isOpen = group?.classList.contains('is-open');
        root.querySelectorAll('[data-hpa-filter].is-open').forEach((openGroup) => openGroup.classList.remove('is-open'));
        if (group && !isOpen) group.classList.add('is-open');
        toggle.setAttribute('aria-expanded', String(!isOpen));
        event.preventDefault();
        return;
      }

      const clear = event.target.closest('[data-hpa-clear]');
      if (clear) {
        FILTERS.forEach((filter) => {
          selections[filter.key] = [];
        });
        if (searchInput) searchInput.value = '';
        apply();
        return;
      }

      if (event.target.closest('[data-hpa-mobile-open]')) {
        mobileDrawer?.classList.add('is-open');
        mobileDrawer?.setAttribute('aria-hidden', 'false');
        mobileOverlay?.classList.add('is-active');
        document.body.classList.add('hpa-filter-drawer-open');
      }

      if (event.target.closest('[data-hpa-mobile-close]') || event.target === mobileOverlay) {
        mobileDrawer?.classList.remove('is-open');
        mobileDrawer?.setAttribute('aria-hidden', 'true');
        mobileOverlay?.classList.remove('is-active');
        document.body.classList.remove('hpa-filter-drawer-open');
      }
    });

    root.addEventListener('change', (event) => {
      const input = event.target.closest('[data-hpa-filter-value]');
      if (input) {
        const key = input.dataset.hpaFilterValue;
        if (input.checked) selections[key].push(input.value);
        else selections[key] = selections[key].filter((value) => value !== input.value);
        apply();
        return;
      }

      const sort = event.target.closest('[data-hpa-sort]');
      if (sort) {
        const url = serializeState();
        url.searchParams.set('sort_by', sort.value);
        window.location.assign(url.toString());
      }
    });

    let searchTimer;
    searchInput?.addEventListener('input', () => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => apply(), 200);
    });

    loadMoreButton?.addEventListener('click', () => {
      const increment = pageSize;
      shownCount += increment;
      if (hasActiveFilters()) showVariantView(true);
      else showProductView(true);
    });

    section?.addEventListener('click', (event) => {
      const link = event.target.closest('.pagination a');
      if (!link) return;
      const href = new URL(link.href, window.location.origin);
      const stateUrl = serializeState();
      URL_KEYS.forEach((key) => href.searchParams.delete(key));
      URL_KEYS.forEach((key) => {
        stateUrl.searchParams.getAll(key).forEach((value) => href.searchParams.append(key, value));
      });
      link.href = href.toString();
    });

    window.addEventListener('popstate', () => {
      readState();
      apply({ updateHistory: false });
    });

    readState();
    apply({ updateHistory: false });
  };

  const boot = () => document.querySelectorAll('[data-hpa-collection-tools]').forEach(initCollectionFilters);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
